import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: ['Mapinga – Bagamoyo, Pwani', 'P.O. Box 35692'],
    },
    {
      icon: Phone,
      title: 'Phone',
      content: ['+255 659 984 488', '+255 760 215 642', '+255 780 635 888', '+255 715 314 267'],
      isPhone: true,
    },
    {
      icon: Mail,
      title: 'Email',
      content: ['admin@bhsp.info', 'manager@bhsp.info'],
      isEmail: true,
    },
    {
      icon: Clock,
      title: 'Operating Hours',
      content: ['Hospital Services:', 'Open 24 Hours – 7 Days a Week (24/7)'],
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-16 md:py-24" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-accent font-medium mb-2">Get in Touch</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Have questions or need assistance? We're here to help. Reach out to us through any of the channels below or fill out the contact form.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-warm" data-testid="contact-info">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <Card key={index} className="bg-white" data-testid={`contact-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-primary mb-2">{item.title}</h3>
                  {item.content.map((line, i) => (
                    item.isEmail ? (
                      <a key={i} href={`mailto:${line}`} className="text-slate-600 text-sm hover:text-primary transition-colors block">{line}</a>
                    ) : item.isPhone ? (
                      <a key={i} href={`tel:${line.replace(/\s/g, '')}`} className="text-slate-600 text-sm hover:text-primary transition-colors block">{line}</a>
                    ) : (
                      <p key={i} className="text-slate-600 text-sm">{line}</p>
                    )
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-spacing" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">
                Send Us a Message
              </h2>
              
              {submitted ? (
                <Card className="bg-teal-light">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-primary text-xl mb-2">
                      Message Sent Successfully!
                    </h3>
                    <p className="text-slate-600 mb-6">
                      Thank you for contacting us. We will get back to you as soon as possible.
                    </p>
                    <Button 
                      onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }} 
                      className="btn-primary"
                      data-testid="contact-send-another"
                    >
                      Send Another Message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Enter your name"
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="Enter your email"
                            required
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            placeholder="+255 123 456 789"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject *</Label>
                          <Input
                            id="subject"
                            value={formData.subject}
                            onChange={(e) => setFormData({...formData, subject: e.target.value})}
                            placeholder="How can we help?"
                            required
                            data-testid="contact-subject-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          placeholder="Write your message here..."
                          rows={6}
                          required
                          data-testid="contact-message-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full btn-primary" 
                        disabled={loading}
                        data-testid="contact-submit-btn"
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                        <Send className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Map */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-6">
                Find Us
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-float h-[400px] lg:h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63405.77775258752!2d38.87194049999999!3d-6.449999999999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c3f00f2d3c91b%3A0xb7b8a7c8e6f3e9a1!2sBagamoyo%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BAOBAB Hospital Location"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-accent" data-testid="contact-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Need Immediate Assistance?
          </h2>
          <p className="text-white/90 mb-8">
            Our emergency services are available 24/7. Don't hesitate to call.
          </p>
          <a href="tel:+255659984488">
            <Button className="bg-white text-accent hover:bg-slate-100 rounded-full px-8 py-4 text-lg font-semibold">
              <Phone className="mr-2 w-5 h-5" />
              Call: +255 659 984 488
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
